import { router, usePage } from "@inertiajs/react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { CheerComposeSheet } from "@/components/crew/cheer-compose-sheet"
import { MomentumRingAvatar } from "@/components/rank/momentum-ring-avatar"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/dialog"
import { usePrefersReducedMotion } from "@/hooks/use-prefers-reduced-motion"
import { cn } from "@/lib/utils"
import {
  reportCreatorFeedCheerPath,
  seenCreatorCheerMomentPath,
} from "@/routes"
import { CHEER_REACTION_META, type PendingCheer } from "@/types/cheers"

interface YourMomentSharedSlice {
  pending_cheer?: PendingCheer | null
  // Index signature satisfies the `usePage<T extends PageProps>` constraint.
  [key: string]: unknown
}

/**
 * OKS-555/OKS-598 — the compose sheet's inputs, snapshotted at cheer-back time.
 *
 * The sheet MUST outlive `pending_cheer`: a late `useRealtimeRefetch`
 * `connected()` catch-up reload can recompute `pending_cheer → null` right
 * after `markSeen()` stamps the moment, and that must NOT unmount an open
 * compose sheet. So we capture everything the sheet needs into local state and
 * render it from this snapshot, independent of the live `pending` prop.
 */
interface CheerBackTarget {
  // OKS-605 — identity of the individual cheer being cheered back. Drives the
  // `<CheerComposeSheet>` React `key` so each distinct moment gets a fresh
  // mount (clean draft/reaction). Must be `kudo_id`, NOT `submissionId`: the
  // same cheerer cheering twice shares one `submissionId` but produces two
  // distinct `kudo_id` moments, so keying on `submissionId` would fail to
  // remount and leak the prior draft.
  kudoId: number
  submissionId: number
  recipient: {
    name: string
    handle: string | null
    avatarUrl: string | null
    rankName: string | null
  }
}

/**
 * OKS-555 — §17 "your moment" cheer celebration modal.
 *
 * Mounted in `creator-shell.tsx` (next to `<BadgeEarnModal>`) so every creator
 * render gets the shared-prop pickup path. Subscribes to
 * `usePage().props.pending_cheer` — the single most-recent un-seen, un-reported
 * cheer the viewer received (display identity + reaction + optional message +
 * cheer-back target), or `null`. Delivered LIVE via the OKS-406
 * `useRealtimeRefetch` partial reload of `pending_cheer` on a
 * `creator.cheer.received` signal (broadcast in-request/rescued, never a naked
 * delayed job).
 *
 * # Open / dismiss
 * Opens when a non-null `pending_cheer` arrives whose `kudo_id` hasn't been
 * dismissed this session. EVERY close path (dismiss, X, backdrop, Esc, and
 * Cheer-back) POSTs `seenCreatorCheerMomentPath` with the `kudo_id` → stamps
 * `moment_seen_at` so `pending_cheer` re-computes to the next un-seen cheer (or
 * null). The close is optimistic — a failed POST just re-surfaces the moment.
 *
 * # Cheer-back (PRD Q4, OKS-943)
 * Opens the `CheerComposeSheet` on the cheerer's most-recent feed post that the
 * viewer has NOT already cheered (`cheer_back_submission_id`). Soft-disabled
 * when no such post exists, with the helper string chosen by
 * `cheer_back_blocked_reason` — `all_cheered` ("you've cheered them all")
 * vs `no_posts` ("they have none yet"). Reusing one string for both would tell
 * the creator something she can see is false (BRIEF §3 R6).
 *
 * ⚠️ OKS-943 — the exclusion is why cheer-back can never land on a post the
 * viewer already cheered. Sending there would UPDATE that cheer in place
 * (`Cheers::Send` upserts), silently rewriting her reaction and nulling her
 * message while the sheet says "Cheer sent". The service is correct; aiming it
 * at an occupied post was not (BRIEF §3 R7).
 *
 * Locked testids: `your-moment-modal`, `cheer-back`, `cheer-report`.
 */
export function YourMomentModal() {
  const { t } = useTranslation()
  const pending = usePage<YourMomentSharedSlice>().props.pending_cheer
  const reducedMotion = usePrefersReducedMotion()

  const [dismissedId, setDismissedId] = useState<number | null>(null)
  const [composeOpen, setComposeOpen] = useState(false)
  // OKS-598 — the cheer-back target is snapshotted (not read live off
  // `pending`) so the compose sheet survives a late `pending_cheer → null`
  // realtime refetch. Once set it stays for the session; `composeOpen` drives
  // the drawer open/close (keeping it mounted preserves the close animation).
  const [cheerBackTarget, setCheerBackTarget] =
    useState<CheerBackTarget | null>(null)
  const open =
    pending != null && pending.kudo_id !== dismissedId && !composeOpen

  const markSeen = (cheer: PendingCheer) => {
    setDismissedId(cheer.kudo_id)
    router.post(
      seenCreatorCheerMomentPath(),
      { kudo_id: cheer.kudo_id },
      { preserveScroll: true, preserveState: true, only: [] },
    )
  }

  const handleOpenChange = (next: boolean) => {
    if (!next && pending) markSeen(pending)
  }

  const handleCheerBack = () => {
    if (pending?.cheer_back_submission_id == null) return
    // Snapshot while `pending` is guaranteed non-null. markSeen's own POST
    // uses `only: []` (returns no props) so it can't null `pending` — but
    // stamping the moment seen means a racing `useRealtimeRefetch` catch-up
    // reload (which DOES carry `pending_cheer` in its `only` list) recomputes
    // it to null. The sheet reads this snapshot, so that late reload can't
    // tear it down.
    setCheerBackTarget({
      kudoId: pending.kudo_id,
      submissionId: pending.cheer_back_submission_id,
      recipient: {
        name: pending.sender_name,
        handle: pending.sender_handle,
        avatarUrl: pending.avatar_url,
        rankName: null,
      },
    })
    setComposeOpen(true)
    markSeen(pending)
  }

  const handleReport = (cheer: PendingCheer) => {
    setDismissedId(cheer.kudo_id)
    router.post(
      reportCreatorFeedCheerPath(cheer.kudo_id),
      {},
      {
        preserveScroll: true,
        preserveState: true,
        only: [],
        onSuccess: () => toast.success(t("cheers.moment.report_done")),
        onError: () => toast.error(t("cheers.moment.report_error")),
      },
    )
  }

  const handle = pending
    ? pending.sender_handle
      ? `@${pending.sender_handle}`
      : pending.sender_name
    : ""
  const meta = pending ? CHEER_REACTION_META[pending.reaction] : null
  const canCheerBack = pending?.cheer_back_submission_id != null

  return (
    <>
      {pending && meta && (
        <Dialog open={open} onOpenChange={handleOpenChange}>
          <DialogContent
            data-testid="your-moment-modal"
            className="sm:max-w-sm"
          >
            <div
              className={cn(
                "flex flex-col items-center gap-3 py-4 text-center",
                !reducedMotion && "motion-safe:animate-rank-pop",
              )}
            >
              <span className="relative inline-flex items-center justify-center">
                <MomentumRingAvatar
                  src={pending.avatar_url}
                  name={pending.sender_name || handle}
                  ring="full"
                  size="lg"
                />
              </span>

              <span
                aria-hidden="true"
                className="text-4xl leading-none"
                data-testid="your-moment-reaction"
              >
                {meta.emoji}
              </span>

              <DialogTitle
                aria-live="polite"
                className="text-2xl leading-tight font-bold text-balance"
              >
                {t("cheers.moment.headline", {
                  name: pending.sender_name || handle,
                  emoji: meta.emoji,
                })}
              </DialogTitle>

              <DialogDescription className="text-balance">
                {t(meta.labelKey)}
              </DialogDescription>

              {pending.message && (
                <p className="bg-muted max-h-32 overflow-y-auto rounded-md px-3 py-2 text-sm break-words whitespace-pre-line">
                  {pending.message}
                </p>
              )}
            </div>

            <DialogFooter className="flex-col gap-2 sm:flex-col sm:justify-center">
              <Button
                data-testid="cheer-back"
                className="w-full"
                disabled={!canCheerBack}
                onClick={handleCheerBack}
              >
                {t("cheers.moment.cheer_back")}
              </Button>
              {!canCheerBack && (
                <p className="text-muted-foreground text-center text-xs">
                  {t(
                    pending.cheer_back_blocked_reason === "all_cheered"
                      ? "cheers.moment.cheer_back_unavailable_all_cheered"
                      : "cheers.moment.cheer_back_unavailable",
                  )}
                </p>
              )}
              <div className="flex items-center justify-center gap-4">
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid="cheer-dismiss"
                  className="text-muted-foreground"
                  onClick={() => handleOpenChange(false)}
                >
                  {t("cheers.moment.dismiss")}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid="cheer-report"
                  className="text-muted-foreground"
                  onClick={() => handleReport(pending)}
                >
                  {t("cheers.moment.report")}
                </Button>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* OKS-598 — rendered from the snapshot, NOT `pending`, so a late
          `pending_cheer → null` realtime refetch (the OKS-555 unmount race)
          can't tear down an open compose sheet mid-cheer-back. */}
      {cheerBackTarget && (
        <CheerComposeSheet
          // OKS-605 — key per moment (kudo_id) forces a fresh mount for each
          // distinct cheer-back, resetting the sheet's init-on-mount draft
          // (message) + reaction so an abandoned draft can't bleed to the next
          // recipient. Keyed on kudo_id, NOT submissionId (see CheerBackTarget).
          key={cheerBackTarget.kudoId}
          open={composeOpen}
          onOpenChange={setComposeOpen}
          submissionId={cheerBackTarget.submissionId}
          recipient={cheerBackTarget.recipient}
          postThumbnailUrl={null}
          postCaption=""
          initialReaction="fire"
          // Cheer-back has no feed post to fill optimistically — the server
          // redirect settles it; the sheet's own Sent confirmation is the cue.
          onOptimisticSend={() => undefined}
          onSendError={() => toast.error(t("crew_feed.cheer.toast_error"))}
        />
      )}
    </>
  )
}
